import { cva, type VariantProps } from "class-variance-authority";
import { type HTMLMotionProps, motion } from "framer-motion";
import React from "react";
import { cls, cn } from "@/lib/utils";
import {
  TypographyH2,
  type TypographyH2Properties,
  TypographyP,
  type TypographyParagraphProperties,
} from "./typography";

//
// Card
//
const cardVariants = cva(cls`rounded-lg shadow-sm`, {
  defaultVariants: {
    variant: "default",
  },
  variants: {
    variant: {
      default: cls`bg-card text-card-foreground`,
      secondary: cls`bg-primary text-primary-foreground`,
      transparent: cls`bg-transparent text-card-foreground border-none shadow-none`,
      triary: cls`bg-primary-foreground text-card-foreground`,
    },
  },
});

export type CardProps = HTMLMotionProps<"div"> &
  VariantProps<typeof cardVariants>;

const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ id, className, variant, ...properties }, reference) => (
    <motion.div
      id={`card${id ? `-${id}` : ""}`}
      ref={reference}
      className={cn(cardVariants({ className, variant }))}
      {...properties}
    />
  ),
);

Card.displayName = "Card";

//
// Card Header
//
export type CardHeaderProps = HTMLMotionProps<"div">;

const CardHeader = React.forwardRef<HTMLDivElement, CardHeaderProps>(
  ({ className, ...properties }, reference) => (
    <motion.div
      ref={reference}
      className={cn("flex flex-col space-y-1.5 p-6", className)}
      {...properties}
    />
  ),
);

CardHeader.displayName = "CardHeader";

//
// Card Title
//
export type CardTitleProps = TypographyH2Properties;

const CardTitle = React.forwardRef<HTMLParagraphElement, CardTitleProps>(
  ({ className, children, ...properties }, reference) => (
    <TypographyH2
      ref={reference}
      className={cn(
        `text-2xl font-semibold leading-none tracking-tight font-prata border-b-0`,
        className,
      )}
      {...properties}
    >
      {children}
    </TypographyH2>
  ),
);

CardTitle.displayName = "CardTitle";

//
// Card Description
//
export type CardDescriptionProps = TypographyParagraphProperties;

const CardDescription = React.forwardRef<
  HTMLParagraphElement,
  CardDescriptionProps
>(({ className, ...properties }, reference) => (
  <TypographyP
    ref={reference}
    className={cn("text-sm text-muted-foreground font-rokkitt", className)}
    {...properties}
  />
));

CardDescription.displayName = "CardDescription";

//
// Card Content
//
const CardContent = React.forwardRef<HTMLDivElement, HTMLMotionProps<"div">>(
  ({ className, ...properties }, reference) => (
    <motion.div
      ref={reference}
      className={cn("p-6 pt-0", className)}
      {...properties}
    />
  ),
);

CardContent.displayName = "CardContent";

//
// Card Footer
//
const CardFooter = React.forwardRef<HTMLDivElement, HTMLMotionProps<"div">>(
  ({ className, ...properties }, reference) => (
    <motion.div
      ref={reference}
      className={cn("flex items-center p-6 pt-0", className)}
      {...properties}
    />
  ),
);

CardFooter.displayName = "CardFooter";

export {
  Card,
  CardHeader,
  CardFooter,
  CardTitle,
  CardDescription,
  CardContent,
};
